import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventTypeChannel } from '@brimo/shared';
import { api } from '../../lib/api';
import { queryRoots } from '../../lib/events';

/** Types of the appointment routes used by the detail page and the outcome dialog (docs/dev/api-appointments.md). */

export interface Person {
  id: string;
  name: string;
  shortName: string;
}

export type AppointmentStatus = 'booked' | 'rescheduled' | 'cancelled' | 'attended' | 'no_show';
export type AppointmentOutcome = 'attended' | 'no_show' | 'rescheduled';

export interface AppointmentCard {
  id: string;
  eventType: { id: string; name: string; systemKey: string | null; colorToken: string | null };
  status: AppointmentStatus;
  statusLabel: string;
  pill: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  timeRangeText: string;
  channel: EventTypeChannel;
  channelLabel: string;
  location: string | null;
  advisor: Person;
  setter: Person | null;
  contact: { id: string; firstName: string; lastName: string; name: string } | null;
  guestName: string | null;
  deal: { id: string; number: number } | null;
  bookedVia: string;
  bookedViaLabel: string;
  outcome: AppointmentOutcome | null;
  outcomeText: string | null;
  conflictOverridden: boolean;
  rescheduledFromAppointmentId: string | null;
  wrapUpTaskId: string | null;
  concern: string | null;
  createdAt: string;
  /** „Laura Brandt: Erstgespräch Kapitalanlage" (sequence `brimo_erstgespraech`, D-377); null otherwise. */
  sequenceTitle: string | null;
}

export interface Delivery {
  id: string;
  channel: 'email' | 'whatsapp' | 'sms';
  label: string;
  sendAt: string;
  sentAt: string | null;
  status: 'scheduled' | 'sent' | 'delivered' | 'opened' | 'failed' | 'cancelled';
  error: string | null;
}

export interface HistoryEntry {
  kind:
    'booked' | 'rescheduled' | 'cancelled' | 'outcome' | 'conflict_overridden' | 'reminder' | 'room_opened';
  title: string;
  text: string | null;
  at: string;
}

/** The video room of a video appointment (meetings module); `canOpen`/`canJoin` are decided by the server. */
export interface AppointmentMeeting {
  id: string;
  status: 'scheduled' | 'lobby' | 'running' | 'ended' | 'processed';
  canOpen: boolean;
  canJoin: boolean;
  reviewHref: string | null;
}

export interface AppointmentDetail extends AppointmentCard {
  reminderText: string | null;
  /** „erteilt am 24.09." · „verweigert" · null (no room / no consent yet). */
  recordingConsent: string | null;
  /** „Kunde nicht beigetreten · Berater 16:00–16:12" (Queue 09). */
  participation: string | null;
  meeting: AppointmentMeeting | null;
  cancellationReason: string | null;
  cancelledBy: 'customer' | 'advisor' | 'system' | null;
  outcomeRecordedAt: string | null;
  rescheduledToAppointmentId: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  deliveries: Delivery[];
  history: HistoryEntry[];
}

/** The follow-up steps of the outcome dialog (Queue 05/06). */
export type OutcomeNextStep =
  | { kind: 'follow_up'; dueOn: string; dueTime?: string; assigneeId?: string; note?: string }
  | {
      kind: 'task';
      title: string;
      type?: 'call' | 'email' | 'whatsapp' | 'document' | 'review' | 'manual';
      dueOn: string;
      dueTime?: string;
      assigneeId?: string;
      priority?: 'a' | 'b' | 'c' | 'd';
      note?: string;
    }
  | { kind: 'book_appointment' }
  | { kind: 'send_self_disclosure' }
  | { kind: 'send_booking_link' }
  | { kind: 'call_tomorrow' }
  | { kind: 'lifecycle_end'; target: 'no_interest' | 'disqualified' | 'dnc'; reason: string };

export interface OutcomeBody {
  outcome: AppointmentOutcome;
  text?: string;
  nextStep?: OutcomeNextStep;
}

export type OutcomeResponse =
  | { next: 'reschedule'; appointmentId: string }
  | {
      next: 'done';
      appointment: AppointmentDetail;
      noShowCount: number;
      noShowsInARow: number;
      secondNoShowInARow: boolean;
      suggestNoInterest: boolean;
      stageSuggestion: string | null;
      createdTaskId: string | null;
    };

export interface RescheduleBody {
  startsAt: string;
  durationMinutes?: number;
  advisorId?: string;
  reason?: string;
  overrideConflict?: boolean;
}

export interface CancelBody {
  reason: string;
  cancelledBy: 'advisor' | 'customer';
  followUp?: { dueOn: string; dueTime?: string; assigneeId?: string; note?: string };
}

export interface Slot {
  startsAt: string;
  endsAt: string;
  advisorId: string;
}

export interface SlotsResponse {
  eventTypeId: string;
  durationMinutes: number;
  days: Array<{ date: string; slots: Slot[] }>;
}

export interface Conflict {
  kind: 'appointment' | 'external' | 'hold';
  title: string;
  startsAt: string;
  endsAt: string;
  overlapMinutes: number;
}

export interface ConflictDetails {
  conflicts: Conflict[];
  alternatives: Slot[];
  dayOverview: Array<{ kind: string; title: string; startsAt: string; endsAt: string }>;
  requested: Slot;
}

export const appointmentKey = (id: string) => [...queryRoots.appointments, 'detail', id] as const;

export function useAppointment(id: string | null) {
  return useQuery({
    queryKey: appointmentKey(id ?? ''),
    queryFn: () => api<AppointmentDetail>(`/appointments/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useSlots(
  params: { eventTypeId: string; from: string; to: string; advisorId: string } | null,
) {
  const search = params
    ? new URLSearchParams({
        eventTypeId: params.eventTypeId,
        from: params.from,
        to: params.to,
        advisorId: params.advisorId,
        internal: '1',
      }).toString()
    : '';
  return useQuery({
    queryKey: [...queryRoots.slots, params ?? {}],
    queryFn: () => api<SlotsResponse>(`/slots?${search}`),
    enabled: params !== null,
  });
}

function useInvalidateAppointments() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryRoots.appointments }),
      queryClient.invalidateQueries({ queryKey: queryRoots.tasks }),
      queryClient.invalidateQueries({ queryKey: queryRoots.queue }),
      queryClient.invalidateQueries({ queryKey: queryRoots.slots }),
      queryClient.invalidateQueries({ queryKey: queryRoots.session }),
    ]);
  };
}

export function useRecordOutcome() {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: OutcomeBody }) =>
      api<OutcomeResponse>(`/appointments/${id}/outcome`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useReschedule() {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RescheduleBody }) =>
      api<AppointmentDetail>(`/appointments/${id}/reschedule`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

export function useCancelAppointment() {
  const invalidate = useInvalidateAppointments();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CancelBody }) =>
      api<AppointmentDetail>(`/appointments/${id}/cancel`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/** Pill tone per status pill text of the screens. */
export function pillVariant(card: AppointmentCard): 'info' | 'warning' | 'success' | 'danger' | 'neutral' {
  if (card.status === 'attended') return 'success';
  if (card.status === 'no_show') return 'danger';
  if (card.status === 'booked') return card.pill === 'Ergebnis fehlt' ? 'warning' : 'info';
  return 'neutral';
}

export function isOverWithoutOutcome(card: AppointmentCard): boolean {
  return card.status === 'booked' && card.pill === 'Ergebnis fehlt';
}
